import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { fetchPublicReceipt } from "@/lib/api";
import { ArrowLeft, ShieldCheck } from "lucide-react";

const Row = ({ label, value }) => (
    <div className="grid grid-cols-12 gap-3 py-3 border-b hairline last:border-b-0">
        <div className="col-span-12 sm:col-span-5">
            <span className="mono-label">{label}</span>
        </div>
        <div className="col-span-12 sm:col-span-7">
            <span
                className="mono-data"
                style={{ fontSize: 12.5, color: "var(--ink)" }}
            >
                {value || "—"}
            </span>
        </div>
    </div>
);

export default function PublicReceipt() {
    const { hash } = useParams();
    const [state, setState] = useState({ loading: true });

    useEffect(() => {
        fetchPublicReceipt(hash)
            .then((r) => setState({ loading: false, data: r }))
            .catch((e) =>
                setState({
                    loading: false,
                    error:
                        e?.response?.status === 404
                            ? "Receipt not found."
                            : "Unable to load receipt.",
                })
            );
    }, [hash]);

    return (
        <main
            className="min-h-screen flex flex-col"
            style={{ background: "var(--bone)" }}
        >
            <header className="border-b hairline">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-6 flex items-center justify-between">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 mono-label"
                        style={{ color: "var(--ink)" }}
                        data-testid="back-to-demo"
                    >
                        <ArrowLeft size={12} strokeWidth={1.5} />
                        HealthCierge
                    </Link>
                    <span className="mono-label">Public-safe receipt</span>
                </div>
            </header>

            <section className="flex-1 py-12">
                <div
                    className="max-w-3xl mx-auto bg-white px-8 sm:px-12 py-10"
                    style={{ border: "4px double var(--ink)" }}
                    data-testid="public-receipt-card"
                >
                    <div className="flex items-center gap-3 border-b hairline pb-5">
                        <ShieldCheck size={18} strokeWidth={1.5} />
                        <div>
                            <p className="mono-label">KATLAS · attestation</p>
                            <h1
                                className="font-serif"
                                style={{
                                    fontSize: 32,
                                    letterSpacing: "-0.02em",
                                    lineHeight: 1.1,
                                }}
                            >
                                Cardiovascular review route receipt
                            </h1>
                        </div>
                    </div>

                    {state.loading && (
                        <p
                            className="mt-8 mono-label"
                            data-testid="receipt-loading"
                        >
                            Loading…
                        </p>
                    )}

                    {state.error && (
                        <div className="mt-8" data-testid="receipt-error">
                            <p className="font-serif" style={{ fontSize: 18 }}>
                                {state.error}
                            </p>
                            <p
                                className="mt-2"
                                style={{ color: "var(--ink-soft)", fontSize: 14 }}
                            >
                                Hash:{" "}
                                <span className="mono-data">{hash}</span>
                            </p>
                        </div>
                    )}

                    {state.data && (
                        <>
                            <div className="mt-8">
                                <Row
                                    label="Receipt hash"
                                    value={state.data.receipt_hash}
                                />
                                <Row
                                    label="Attestation reference"
                                    value={state.data.attestation_reference}
                                />
                                <Row
                                    label="Signer issuer"
                                    value={state.data.signer?.issuer}
                                />
                                <Row
                                    label="Signer key id"
                                    value={state.data.signer?.key_id}
                                />
                                <Row
                                    label="Signer fingerprint"
                                    value={state.data.signer_fingerprint}
                                />
                                <Row
                                    label="Node timestamp"
                                    value={state.data.node_timestamp_utc}
                                />
                                <Row
                                    label="Verification"
                                    value={state.data.verification}
                                />
                                <Row
                                    label="Outcome"
                                    value={state.data.action_outcome}
                                />
                                <Row
                                    label="Review route"
                                    value={
                                        state.data.review_route_category
                                    }
                                />
                                <Row
                                    label="Acting role"
                                    value={state.data.acting_role?.label}
                                />
                                {state.data.receipt_type ===
                                    "healthcierge_sensitive_disclosure_route" && (
                                    <div data-testid="public-receipt-disclosure-block">
                                        <Row
                                            label="Signal category"
                                            value={state.data.signal_category}
                                        />
                                        <Row
                                            label="Disclosure lock applied"
                                            value={String(
                                                state.data.disclosure_lock_applied
                                            )}
                                        />
                                        <Row
                                            label="Clinician route choice"
                                            value={
                                                state.data.clinician_route_choice
                                            }
                                        />
                                        <Row
                                            label="Patient-facing message"
                                            value={
                                                state.data
                                                    .patient_facing_message_type
                                            }
                                        />
                                        <Row
                                            label="Carer involvement"
                                            value={
                                                state.data
                                                    .nominated_carer_involvement_status
                                            }
                                        />
                                    </div>
                                )}
                                <Row
                                    label="Shared evidence refs"
                                    value={(
                                        state.data.shared_evidence_refs || []
                                    )
                                        .map((e) => e.ref)
                                        .join("  ·  ")}
                                />
                                <Row
                                    label="Withheld evidence count"
                                    value={String(
                                        state.data.withheld_evidence_count
                                    )}
                                />
                                <Row
                                    label="Event"
                                    value={`${state.data.event_id} · ${state.data.policy_id}`}
                                />
                            </div>

                            <div className="mt-8 panel-alt px-5 py-4">
                                <p
                                    className="font-serif"
                                    style={{
                                        fontSize: 15,
                                        color: "var(--ink)",
                                        lineHeight: 1.55,
                                    }}
                                >
                                    {state.data.data_boundary_statement}
                                </p>
                                <p className="mono-label mt-3">
                                    Custody · Authority · Receipts
                                </p>
                            </div>
                        </>
                    )}
                </div>
            </section>

            <footer className="border-t hairline">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 py-6 flex items-center justify-between">
                    <span className="mono-label">
                        HealthCierge · KATLAS demonstrator
                    </span>
                    <span className="mono-label">
                        Public fields only · no raw data
                    </span>
                </div>
            </footer>
        </main>
    );
}
